import React from 'react';
import { Alert, Select } from 'antd';
import styled from 'styled-components';

import {
  countUnmatched,
  rowKey,
  type ImportAssignment,
} from '../utils/importOps';
import type { EpiInspectResult } from '../types/types';

/** Enough to recognise what is going, without burying the dialog. */
const LOST_SHOWN = 6;

interface IProps {
  result: EpiInspectResult;
  /** The document this was read from — the ePI's own name is no clue to it. */
  fileName: string;
  /** The one composition the file was read as, or none for a whole ePI. */
  compositionName?: string;
  products: { id: string; name: string }[];
  assignment: ImportAssignment;
  onAssign: (key: string, productIds: string[]) => void;
  /** Authored fields in the other language that the import carries across. */
  kept: number;
  /** Authored fields whose section this document does not have. */
  dropped: number;
  /** The sections those fields belong to, named in the language being lost. */
  lost: string[];
  /** Custom sections from the document not imported, in its own language. */
  skipped: string[];
  otherLanguageName: string;
  importLanguageName: string;
  problem: string | null;
}

/**
 * What the document turned out to be, and the one question only the user can
 * answer: which products each of its documents covers.
 */
const ImportReview: React.FC<IProps> = ({
  result,
  fileName,
  compositionName,
  products,
  assignment,
  onAssign,
  kept,
  dropped,
  lost,
  skipped,
  otherLanguageName,
  importLanguageName,
  problem,
}) => {
  const options = products.map(p => ({ value: p.id, label: p.name }));
  const count = result.compositions.length;
  const documents = `${count} document${count === 1 ? '' : 's'}`;
  const unmatched = countUnmatched(result);

  return (
    <>
      <Source>
        Read from <strong>{fileName}</strong>
      </Source>

      <Alert
        type="warning"
        showIcon
        message={
          compositionName
            ? `Importing replaces the ${compositionName} documents in this ePI`
            : 'Importing replaces every document in this ePI'
        }
        description={
          compositionName
            ? `The ${documents} in this file take their place. The other documents, the ePI's name, product family and products stay as they are.`
            : `The ${documents} in this file take their place. The ePI's name, product family and products stay as they are.`
        }
      />

      {dropped > 0 && (
        <Spaced>
          <Alert
            type="error"
            showIcon
            message={`${dropped} ${otherLanguageName} field${dropped === 1 ? '' : 's'} written here will be lost`}
            description={
              <>
                Both languages must have the same sections. These belong to
                sections this document does not have, or to custom sections and
                packaging blocks whose number differs under the same heading, so
                they cannot be paired.
                {kept > 0 && ` The other ${kept} are kept.`}
                <Lost>
                  {lost.slice(0, LOST_SHOWN).map((name, i) => (
                    <li key={i}>{name}</li>
                  ))}
                </Lost>
                {lost.length > LOST_SHOWN && (
                  <More>and {lost.length - LOST_SHOWN} more</More>
                )}
              </>
            }
          />
        </Spaced>
      )}

      {skipped.length > 0 && (
        <Spaced>
          <Alert
            type="error"
            showIcon
            message={`${skipped.length} ${importLanguageName} custom section${skipped.length === 1 ? '' : 's'} from this document will not be imported`}
            description={
              <>
                Both languages must have the same sections. Where these sit, the
                ePI has a different number of {otherLanguageName} custom
                sections, so those stay and these are left out.
                <Lost>
                  {skipped.slice(0, LOST_SHOWN).map((name, i) => (
                    <li key={i}>{name}</li>
                  ))}
                </Lost>
                {skipped.length > LOST_SHOWN && (
                  <More>and {skipped.length - LOST_SHOWN} more</More>
                )}
              </>
            }
          />
        </Spaced>
      )}

      {dropped === 0 && kept > 0 && (
        <Spaced>
          <Alert
            type="success"
            showIcon
            message={`${kept} ${otherLanguageName} field${kept === 1 ? '' : 's'} written here are kept`}
            description={`A document is written in one language. The ${otherLanguageName} text already here is carried across onto the sections it belongs to.`}
          />
        </Spaced>
      )}

      {unmatched > 0 && (
        <Spaced>
          <Alert
            type="info"
            showIcon
            message={`${unmatched} imported heading${unmatched === 1 ? ' is' : 's are'} not a predefined section`}
            description="Each is imported as a custom section of its own, where the document printed it, and marked in the menu. Keep it as a custom section or delete it."
          />
        </Spaced>
      )}

      {result.paragraphsAfterTheEpi > 0 && (
        <Spaced>
          <Alert
            type="info"
            showIcon
            message={`${result.paragraphsAfterTheEpi} paragraphs after the end of the ePI are not imported`}
            description="The file carries content past where the product information ends — an Instructions for Use booklet, usually. It belongs to no section, so the ePI will not hold it."
          />
        </Spaced>
      )}

      {result.compositions.map(report => {
        const key = rowKey(report.type, report.order);
        return (
          <Row key={key}>
            <RowTitle>{report.title}</RowTitle>
            <Designations>
              {report.designations.length > 0
                ? report.designations.join(' · ')
                : 'This document names no products of its own.'}
            </Designations>
            <Select
              mode="multiple"
              value={assignment[key] ?? []}
              onChange={ids => onAssign(key, ids)}
              options={options}
              placeholder="Which products this document covers"
              style={{ width: '100%' }}
            />
          </Row>
        );
      })}

      {problem && (
        <Spaced>
          <Alert type="error" showIcon message={problem} />
        </Spaced>
      )}
    </>
  );
};

export default ImportReview;

const Source = styled.div`
  margin-bottom: 12px;
  font-size: 13px;
  color: ${({ theme }) => theme?.colors?.textSecondary || '#475067'};
  overflow-wrap: anywhere;
`;

const Lost = styled.ul`
  margin: 8px 0 0;
  padding-left: 18px;
`;

const More = styled.div`
  margin-top: 4px;
  font-style: italic;
`;

const Spaced = styled.div`
  margin-top: 12px;
`;

const Row = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px 0;
  border-top: 1px solid
    ${({ theme }) => theme?.colors?.borderLight || '#e6e8ee'};

  &:first-of-type {
    margin-top: 16px;
  }
`;

const RowTitle = styled.div`
  font-weight: 600;
  color: ${({ theme }) => theme?.colors?.textPrimary || '#0f1729'};
`;

const Designations = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme?.colors?.textSecondary || '#475067'};
`;
